import React, { FC } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const EXTERNAL_TECHNICIAN_ALLOWED_PATH_PREFIXES = [
  '/home',
  '/logout',
  '/service-maintenance/dispatch/myMaintenancePlannings',
  '/service-maintenance/interventionFile/new',
  '/service-maintenance/interventionFile/edit',
  '/service-maintenance/interventionFile/preview',
  '/service-maintenance/interventionFiles',
  '/kpi/uo-rapoarte',
];

const UO_REPORTS_PATH = '/kpi/uo-rapoarte';
const UO_REPORTS_ALLOWED_ROLES = ['admin', 'external_tehnician'];

export const isExternalTechnicianPathAllowed = (pathname: string): boolean => {
  return EXTERNAL_TECHNICIAN_ALLOWED_PATH_PREFIXES.some(
    (allowedPath) => pathname === allowedPath || pathname.startsWith(`${allowedPath}/`),
  );
};

const PrivateRoute: FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated, profile } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to='/home' replace />;
  }

  const roles = profile?.userPermission?.rols || [];
  const isExternalOnly =
    roles.includes('external_tehnician') &&
    !roles.some((role: string) =>
      ['admin', 'manager', 'dispatcher', 'senior_tehnician', 'tehnician', 'project_team_lead'].includes(role),
    );

  if (isExternalOnly && !isExternalTechnicianPathAllowed(location.pathname)) {
    return <Navigate to='/service-maintenance/dispatch/myMaintenancePlannings' replace />;
  }

  const isUoReportsPath =
    location.pathname === UO_REPORTS_PATH || location.pathname.startsWith(`${UO_REPORTS_PATH}/`);
  const canAccessUoReports = roles.some((role: string) => UO_REPORTS_ALLOWED_ROLES.includes(role));

  if (isUoReportsPath && !canAccessUoReports) {
    return <Navigate to='/home' replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;
